import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { FolderCreationModeContext, SetFolderCreationModeContext, QuantAnalysisContext, QuantAnalysisVersionContext } from '@trinity/chat-components';
import { useInvalidateQuantFolders } from '@trinity/chat-components/hooks/useInvalidateQuantFolders';
import { FormButtons } from '@trinity/chat-components/components/presentation/FormButtons/index.js';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Folder } from 'react-feather';
import { Input } from '@/components/presentation/Input';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';

export const CreateFolderInput = () => {
  const folderCreationMode = useContext(FolderCreationModeContext);
  const setFolderCreationMode = useContext(SetFolderCreationModeContext);
  const quantAnalysis = useContext(QuantAnalysisContext);
  const quantVersion = useContext(QuantAnalysisVersionContext);
  const [folderName, setFolderName] = useState('');
  const project = useContext(ProjectDetailsContext);
  const apiClient = useChatApiClient();
  const invalidateQuantFolders = useInvalidateQuantFolders();
  const projectId = project.query.data?.project?.id;
  const quantAnalysisId = quantAnalysis?.id;
  const quantVersionId = quantVersion?.id;

  const handleFolderNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
  }, []);

  const createFolderMutation = useMutation({
    mutationFn: () => {
      return apiClient.quantAnalysis.createQuantFolder({
        projectId,
        quantAnalysisId,
        name: folderName,
        parentId: null,
        versionId: quantVersionId,
      });
    },
    onSuccess: () => {
      invalidateQuantFolders(quantAnalysisId, projectId, quantVersionId);
      setFolderName('');
      setFolderCreationMode(false);
      Toast.success({
        title: 'Folder created successfully',
      });
    },
    onError: () => {
      Toast.error({
        title: 'There was an error creating the folder',
      });
    },
  });

  const { mutate: createFolder } = createFolderMutation;
  const handleFolderSubmit = useCallback(() => {
    if (folderName.trim() && quantAnalysisId) {
      createFolder();
    }
  }, [folderName, quantAnalysisId, createFolder]);

  const handleFolderCancel = useCallback(() => {
    setFolderName('');
    setFolderCreationMode(false);
  }, [setFolderCreationMode]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && folderName.trim()) {
      handleFolderSubmit();
    } else if (e.key === 'Escape') {
      handleFolderCancel();
    }
  }, [folderName, handleFolderSubmit, handleFolderCancel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (!folderCreationMode || !quantAnalysisId) {
    return null;
  }

  return (
    <FolderContainer onMouseDown={handleMouseDown} onClick={handleClick}>
      <Folder size={24} />
      <Input
        placeholder="Enter folder name..."
        value={folderName}
        onChange={handleFolderNameChange}
        onKeyDown={handleKeyDown}
        autoFocus />
      <FormButtons
        size={10}
        disabled={folderName.trim().length < 1 || !quantAnalysisId || createFolderMutation.isPending}
        onCancel={handleFolderCancel}
        onSubmit={handleFolderSubmit} />
    </FolderContainer>
  );
};

const FolderContainer = styled.div`
  margin: 5px 10px 5px 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
